<template>
  <div>
    <div :class="containerClass">
      <page-header
        :title="title"
        :container-class="null"
        :show-back="true"
        class="mb-32pt"
      />
    </div>
    <div class="page-section">
      <div :class="containerClass">
        <!-- <b-card> -->
          <b-overlay :show="isLoading" rounded="sm" opacity="0.4">
            <template #overlay>
              <div class="text-center">
                <b-spinner class="mb-2" />
                <div>Loading transcript...</div>
              </div>
            </template>

            <template #header>
              <div class="gradebook-header d-flex justify-content-between align-items-center flex-wrap">
                <div class="d-flex align-items-center">
                  <user-avatar
                    :fileUrl="get(studentDetails, 'avatar')"
                    size="lg"
                    class="mr-3"
                  />
                  <div>
                    <h4 class="mb-1 font-weight-semibold">
                      {{ studentFullName }}
                    </h4>
                    <div class="text-muted">
                      {{ get(studentDetails, 'email', '-') }}
                    </div>
                  </div>
                </div>
                <div class="text-right header-progress">
                  <div class="text-muted text-uppercase tracking-wide small mb-1">Course Completion</div>
                  <div class="h2 font-weight-bold mb-0">{{ overallProgressLabel }}</div>
                </div>
              </div>
            </template>

            <div class="gradebook-surface">
              <section class="profile-card mb-4 text-center">
                <div class="summary-identification">
                  <user-avatar
                    :fileUrl="get(studentDetails, 'avatar')"
                    size="xl"
                    class="summary-avatar"
                  />
                </div>
                <div class="summary-info">
                  <div class="summary-name">{{ studentFullName }}</div>
                  <div class="summary-email">{{ get(studentDetails, 'email', '-') }}</div>
                </div>
                <div class="summary-meta-row">
                  <div class="summary-chip">
                    <span class="chip-label">Enrollment Status</span>
                    <span class="chip-value">{{ enrollmentStatusLabel }}</span>
                  </div>
                  <div class="summary-chip">
                    <span class="chip-label">Start Date</span>
                    <span class="chip-value">{{ formatDate(transcript.started_at) }}</span>
                  </div>
                  <div class="summary-chip">
                    <span class="chip-label">Completion Date</span>
                    <span class="chip-value">{{ get(transcript, 'completion_date', '-') }}</span>
                  </div>
                  <div class="summary-chip" :class="gradeChipClass">
                    <span class="chip-label">Overall Grade</span>
                    <span class="chip-value">{{ overallGradeLabel }}</span>
                  </div>
                </div>
              </section>

              <section class="overall-progress-card mb-4">
                <div class="overall-progress-header">
                  <div>
                    <p class="label">Progress</p>
                    <p class="value">{{ overallProgressPercentage }}</p>
                    <p class="caption">of the course completed</p>
                  </div>
                  <div class="text-right">
                    <p class="label">Overall Grade</p>
                    <p class="value">{{ overallGradeLabel }}</p>
                    <p class="caption">{{ overallGradeCaption }}</p>
                  </div>
                </div>
                <div class="overall-progress-bar">
                  <div class="overall-progress-fill" :style="{ width: overallProgressPercentage }"></div>
                </div>
                <div class="overall-progress-meta">
                  <div class="meta-item">
                    <i class="far fa-clock mr-1"></i>
                    Total Active Time: {{ formattedEstimatedTime || '—' }}
                  </div>
                  <div class="meta-item text-right">
                    {{ overallProgressDescription }}
                  </div>
                </div>
              </section>

              <!--
                State compliance seat time (CA CNA / CDPH E276). Rendered only
                for courses that opted in, so every other course's transcript
                looks exactly as it did before.
              -->
              <section v-if="showComplianceSeatTime" class="compliance-card mb-4">
                <div class="compliance-header">
                  <div>
                    <p class="label">
                      <i class="fas fa-shield-alt mr-1"></i>
                      Theory Hours
                    </p>
                    <p class="value">
                      {{ theoryHoursEarned }}
                      <span v-if="requiredTheoryHours" class="value-target"
                        >/ {{ requiredTheoryHours }}</span
                      >
                      <span class="value-unit">hrs</span>
                    </p>
                  </div>
                  <div class="compliance-basis">
                    <span class="basis-pill">
                      {{ minutesPerCreditHour }} min = 1 credit hour
                    </span>
                  </div>
                </div>

                <div v-if="requiredTheoryHours" class="compliance-progress-bar">
                  <div
                    class="compliance-progress-fill"
                    :class="{ complete: theoryHoursComplete }"
                    :style="{ width: theoryHoursPercentage }"
                  ></div>
                </div>

                <div class="compliance-split">
                  <div class="split-item">
                    <span class="split-label">
                      <i class="far fa-file-alt mr-1"></i>Reading
                    </span>
                    <span class="split-value">{{ formattedReadTime }}</span>
                  </div>
                  <div class="split-item">
                    <span class="split-label">
                      <i class="fas fa-play-circle mr-1"></i>Video
                    </span>
                    <span class="split-value">{{ formattedWatchedTime }}</span>
                  </div>
                  <div class="split-item split-item--muted">
                    <span class="split-label">
                      <i class="far fa-clock mr-1"></i>Total Active
                    </span>
                    <span class="split-value">{{ formattedEstimatedTime || '—' }}</span>
                  </div>
                </div>

                <p class="compliance-note">
                  Reading and video are measured seat time. Total Active also
                  includes estimated time for assignments and assessments.
                </p>
              </section>

              <section class="activity-module">
                <div class="gradebook-tabs">
                  <button
                    v-for="tab in activityTabs"
                    :key="tab.key"
                    class="gradebook-tab"
                    :class="{ active: activeTab === tab.key }"
                    @click="handleTabClick(tab.key)"
                  >
                    <i :class="tab.icon"></i>
                    <div class="tab-copy">
                      <span class="tab-label">{{ tab.label }}</span>
                      <span class="tab-count">{{ tab.countLabel }}</span>
                    </div>
                  </button>
                </div>
                <div v-if="activeTabMeta" class="gradebook-tab-meta">
                  <span v-if="activeTabMeta.time">
                    <i class="far fa-clock mr-1"></i>
                    {{ activeTabMeta.time }}
                  </span>
                  <span v-if="activeTabMeta.count">
                    <i class="far fa-list-alt mr-1"></i>
                    {{ activeTabMeta.count }}
                  </span>
                </div>

                <div v-if="showChapterTable" class="chapter-progress-section">
                  <!-- Skeleton rather than a text line: it holds the layout
                       steady so the card doesn't jump when data lands. -->
                  <div
                    v-if="chapterProgressLoading || overallProgressLoading"
                    class="skeleton-block"
                  >
                    <div class="skeleton-row" v-for="n in 4" :key="`cp-${n}`">
                      <div class="skeleton-line skeleton-line--title"></div>
                      <div class="skeleton-line skeleton-line--meta"></div>
                    </div>
                  </div>
                  <div v-else-if="chapterProgressList.length === 0" class="empty-state">
                    No chapter progress yet.
                  </div>
                  <div v-else class="consolidated-progress-card">
                    
                    <!-- Analytics Section -->
                    <div v-if="overallProgressSummary" class="analytics-section">
                      <div class="analytics-item">
                        <div class="analytics-icon video">
                          <i class="fas fa-play-circle"></i>
                        </div>
                        <div class="analytics-content">
                          <div class="analytics-label">Videos Watched</div>
                          <div class="analytics-value">
                            {{ get(overallProgressSummary, 'videos_watched.completed', 0) }}/{{ get(overallProgressSummary, 'videos_watched.total', 0) }}
                          </div>
                          <div class="analytics-percentage">
                            {{ formatPercent(get(overallProgressSummary, 'videos_watched.percentage', 0)) }}%
                          </div>
                        </div>
                      </div>

                      <div class="analytics-item">
                        <div class="analytics-icon assignment">
                          <i class="fas fa-file-alt"></i>
                        </div>
                        <div class="analytics-content">
                          <div class="analytics-label">Assignments Done</div>
                          <div class="analytics-value">
                            {{ get(overallProgressSummary, 'assignments_done.completed', 0) }}/{{ get(overallProgressSummary, 'assignments_done.total', 0) }}
                          </div>
                          <div class="analytics-percentage">
                            {{ formatPercent(get(overallProgressSummary, 'assignments_done.percentage', 0)) }}%
                          </div>
                        </div>
                      </div>

                      <div class="analytics-item">
                        <div class="analytics-icon quiz">
                          <i class="fas fa-clipboard-check"></i>
                        </div>
                        <div class="analytics-content">
                          <div class="analytics-label">Assessments Passed</div>
                          <div class="analytics-value">
                            {{ get(overallProgressSummary, 'quizzes_passed.completed', 0) }}/{{ get(overallProgressSummary, 'quizzes_passed.total', 0) }}
                          </div>
                          <div class="analytics-percentage">
                            {{ formatPercent(get(overallProgressSummary, 'quizzes_passed.percentage', 0)) }}%
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Chapter Progress List -->
                    <div class="chapter-progress-list">
                      <div
                        v-for="(chapter, chapterIndex) in chapterProgressList"
                        :key="chapter.id"
                        class="chapter-progress-row"
                        :class="{
                          'continue-here-row': chapterIndex === continueChapterIndex,
                          'locked-row': isChapterLocked(chapter, chapterIndex),
                        }"
                      >
                        <span
                          v-if="chapterIndex === continueChapterIndex"
                          class="continue-here-badge"
                        >
                          Continue Here
                        </span>
                        <div class="chapter-row-title">
                          <i
                            v-if="isChapterLocked(chapter, chapterIndex)"
                            class="fas fa-lock chapter-row-lock"
                            title="Locked until previous chapters are completed"
                          ></i>
                          {{ chapter.title || '-' }}
                        </div>
                        <div class="chapter-row-progress-column">
                          <div class="chapter-row-progress-bar" :class="{ 'completed': chapter.progress_percent >= 100 }">
                            <div 
                              class="chapter-row-progress-fill" 
                              :style="{ width: `${Math.min(chapter.progress_percent || 0, 100)}%` }"
                              :class="{ 'completed': chapter.progress_percent >= 100 }"
                            ></div>
                          </div>
                          <span v-if="chapter.sum_of_calculated_time_minutes" class="chapter-row-time">
                            <i class="far fa-clock"></i>
                            {{ formatEstimatedMinutes(chapter.sum_of_calculated_time_minutes) }}
                          </span>
                        </div>
                        <div class="chapter-row-percentage">{{ formatPercent(chapter.progress_percent || 0) }}%</div>
                        <div class="chapter-row-lessons">{{ formatChapterLessons(chapter) }} lessons</div>

                      </div>

                      <!-- Final Exam row -->
                      <div
                        v-if="chapterProgressFinalExam"
                        :key="chapterProgressFinalExam.id"
                        class="chapter-progress-row final-exam-row"
                      >
                        <div class="chapter-row-title">
                          {{ chapterProgressFinalExam.title || 'Final Exam' }}
                          <span class="final-exam-badge">Final Exam</span>
                        </div>
                        <div class="chapter-row-progress-column">
                          <div class="chapter-row-progress-bar" :class="{ 'completed': chapterProgressFinalExam.passed }">
                            <div
                              class="chapter-row-progress-fill"
                              :style="{ width: `${chapterProgressFinalExam.attempted ? 100 : 0}%` }"
                              :class="{ 'completed': chapterProgressFinalExam.passed }"
                            ></div>
                          </div>
                        </div>
                        <div class="chapter-row-percentage"></div>
                        <div class="chapter-row-lessons">
                          {{ chapterProgressFinalExam.attempted ? (chapterProgressFinalExam.passed ? 'Passed' : 'Failed') : 'Pending' }}
                        </div>
                      </div>
                    </div>


                  </div>
                </div>

                <div v-else-if="showSubmissionsTable" class="submissions-table">
                  <!-- Filters -->
                  <div class="submissions-filters">
                    <div class="filter-group">
                      <label class="filter-label">Status:</label>
                      <b-form-select
                        v-model="submissionsStatusFilter"
                        :options="submissionsStatusOptions"
                        size="sm"
                        class="filter-select"
                      />
                    </div>
                    <div class="filter-group">
                      <label class="filter-label">Type:</label>
                      <b-form-select
                        v-model="submissionsTypeFilter"
                        :options="submissionsTypeOptions"
                        size="sm"
                        class="filter-select"
                      />
                    </div>
                    <div class="filter-group">
                      <label class="filter-label">Filter:</label>
                      <b-form-select
                        v-model="submissionsApiFilter"
                        :options="submissionsApiFilterOptions"
                        size="sm"
                        class="filter-select"
                      />
                    </div>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="export-excel-btn ml-auto"
                      :disabled="exportingExcel || submissionsRows.length === 0"
                      @click="exportSubmissionsToExcel"
                    >
                      <b-spinner v-if="exportingExcel" small class="mr-1" />
                      <i v-else class="fas fa-file-excel mr-1"></i>
                      Export to Excel
                    </b-button>
                    
                  </div>

                  <div v-if="submissionsLoading" class="skeleton-block">
                    <div class="skeleton-row" v-for="n in 5" :key="`sub-${n}`">
                      <div class="skeleton-line skeleton-line--title"></div>
                      <div class="skeleton-line skeleton-line--meta"></div>
                    </div>
                  </div>
                  <div v-else-if="submissionsRows.length === 0" class="empty-state">
                    No submissions yet.
                  </div>
                  <div v-else class="submissions-table-container">
                    <table class="submissions-table-grid">
                      <colgroup>
                        <col class="col-title" />
                        <col class="col-status" />
                        <col class="col-weight" />
                        <col class="col-score" />
                        <col class="col-time" />
                        <col v-if="showActionColumn" class="col-action" :class="{ 'col-action--narrow': isSuperAdmin }" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Status</th>
                          <th>Weight</th>
                          <th>Score</th>
                          <th>Total Active Time</th>
                          <th v-if="showActionColumn" class="action-head">Action</th>
                        </tr>
                      </thead>
                      <tbody
                        v-for="(section, sectionIndex) in submissionsSectionRows"
                        :key="`section-${section.sectionId || sectionIndex}`"
                      >
                        <tr
                          class="submission-section-row"
                        >
                          <td :colspan="showActionColumn ? 6 : 5" class="submission-section-cell">
                            <div class="submission-section-title">{{ section.sectionTitle || 'Untitled Lecture' }}</div>
                          </td>
                        </tr>

                        <tr
                          v-for="row in section.items"
                          :key="`${row.submissionType}-${row.id || row.assignment_id || row.quiz_id}-${row._rowIndex}`"
                          class="submissions-table-row"
                        >
                          <td class="submission-title">
                            <p class="lesson-title">{{ row.lesson_title || 'Chapter Level' }}</p>
                            <p class="title">
                              {{ row.title || '-' }}
                            </p>
                          </td>
                          <td class="submission-status">
                            <div class="status-wrapper">
                              <span v-if="row.status" class="status-badge" :class="row.status.toLowerCase()">
                                {{ formatStatus(row.status) }}
                              </span>
                              <span v-else class="status-badge pending">Pending</span>
                              <span v-if="row.status && row.status.toLowerCase() === 'completed' && row.completed" class="completed-date">
                                {{ formatDateTime(row.completed) }}
                              </span>
                            </div>
                          </td>
                          <td class="submission-weight">
                            {{ formatWeightValue(row.weight) }}
                          </td>
                          <td class="submission-score">
                            {{ formatSubmissionScore(row) }}
                          </td>
                          <td class="submission-time">
                            {{ formatEstimatedTime(row.estimated_time_minutes) }}
                          </td>
                          <td v-if="showActionColumn" class="submission-action">
                            <!-- Fixed-width action slot so every row's right edge
                                 aligns whether or not it carries the grant affordance. -->
                            <div class="submission-actions-wrapper" :class="{ 'grant-only': isSuperAdmin }">
                              <div
                                v-if="showRowActions(row)"
                                class="action-split"
                                :class="{ 'has-grant': !isSuperAdmin && canGrantAttempts(row) }"
                              >
                                <!-- Super admins have no access to the attempts
                                     screens, so they only get the grant control. -->
                                <b-button
                                  v-if="!isSuperAdmin"
                                  size="sm"
                                  variant="light"
                                  class="view-attempts-btn"
                                  @click="openSubmissionAttempts(row)"
                                >
                                  View Attempts
                                </b-button>
                                <!-- Assessments only: unblock a student who has used
                                     every attempt without passing. On courses that
                                     require a passing grade to advance they cannot
                                     progress otherwise. -->
                                <b-button
                                  v-if="canGrantAttempts(row)"
                                  size="sm"
                                  variant="light"
                                  class="grant-attempts-btn"
                                  v-b-tooltip.hover
                                  :title="grantAttemptsTooltip(row)"
                                  :aria-label="grantAttemptsTooltip(row)"
                                  @click="openGrantAttempts(row)"
                                >
                                  <i class="material-icons">add</i>
                                </b-button>
                              </div>
                              <span v-else-if="!isSuperAdmin" class="dash">-</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div v-else-if="isVideoTab" class="video-table">
                  <div
                    v-if="(activeTab === 'library_videos' && libraryVideosLoading) || (activeTab === 'videos' && lectureVideosLoading)"
                    class="skeleton-block"
                  >
                    <div class="skeleton-row" v-for="n in 4" :key="`vid-${n}`">
                      <div class="skeleton-line skeleton-line--title"></div>
                      <div class="skeleton-line skeleton-line--meta"></div>
                    </div>
                  </div>
                  <div v-else-if="videoRows.length === 0" class="empty-state">No videos yet.</div>
                  <div v-else class="video-table-container">
                    <div class="video-table-content">
                      <div class="video-table-head">
                        <span>Title</span>
                        <span>Watched at</span>
                      </div>

                      <div
                        v-for="(activity, index) in videoRows"
                        :key="`${activity.id || activity.lesson_id || index}-${index}`"
                        class="video-table-row"
                      >
                        <div class="video-title-cell">
                          <p class="title">{{ cleanIndexedLabel(get(activity, 'skill_library.title') || activity.title || activity.video_title) }}</p>
                        </div>
                        <div class="video-date-cell">
                          <span v-if="activityCompletedDisplay(activity)">{{ activityCompletedDisplay(activity) }}</span>
                          <span v-else class="dash">-</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="card-footer"
                    v-if="activeTab === 'library_videos' && libraryVideosTotal > libraryVideosPerPage"
                  >
                    <pagination
                      v-model="libraryVideosCurrentPage"
                      :total-rows="libraryVideosTotal"
                      :per-page="libraryVideosPerPage"
                      @change="onLibraryVideosPageChange"
                    />
                  </div>
                </div>
              </section>
            </div>
          </b-overlay>
        <!-- </b-card> -->

      </div>
    </div>

    <!-- Grant extra attempts (assessments only) -->
    <b-modal
      v-model="showGrantAttemptsModal"
      title="Add Assessment Attempts"
      centered
      :ok-disabled="grantAttemptsSubmitting || !grantAttemptsCount"
      :cancel-disabled="grantAttemptsSubmitting"
      ok-title="Add Attempts"
      @ok.prevent="submitGrantAttempts"
    >
      <div v-if="grantAttemptsRow">
        <p class="mb-3">
          <strong>{{ grantAttemptsRow.title }}</strong>
          <span class="text-muted d-block small">
            {{ grantAttemptsRow.lesson_title || grantAttemptsRow.section_title || 'Course level' }}
          </span>
        </p>

        <div class="d-flex justify-content-between mb-3 p-2 bg-light rounded small">
          <span>Attempts used: <strong>{{ grantAttemptsRow.attempts_count || 0 }}</strong></span>
          <span>Limit: <strong>{{ grantAttemptsRow.max_attempts || 0 }}</strong></span>
          <span>
            Already granted: <strong>{{ grantAttemptsRow.extra_attempts_granted || 0 }}</strong>
          </span>
        </div>

        <b-form-group label="Additional attempts" label-for="grant_attempts_count" label-class="form-label">
          <b-form-input
            id="grant_attempts_count"
            v-model.number="grantAttemptsCount"
            type="number"
            min="1"
            max="5"
          />
          <small class="text-muted">
            Between 1 and 5. This is added to the existing limit; previous attempts and
            scores are kept.
          </small>
        </b-form-group>

        <b-form-group label="Reason (optional)" label-for="grant_attempts_reason" label-class="form-label">
          <b-form-textarea
            id="grant_attempts_reason"
            v-model="grantAttemptsReason"
            rows="2"
            placeholder="e.g. Technical failure during the second attempt"
          />
        </b-form-group>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { USER_ROLES } from '@/common/constants';
import { formatDateTime, formatMinutesToHoursAndMinutes, formatShortDate } from '@/common/utils';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import UserAvatar from '@/components/User/UserAvatar.vue';
import axios from 'axios';
import { get, startCase } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CourseEnrollmentGradebook',
  components: {
    PageHeader,
    UserAvatar,
    Pagination,
  },
  extends: Page,
  data() {
    return {
      transcript: {},
      isLoading: false,
      title: 'Student Progress',
      activeTab: 'progress',
      showAssignmentTasksModal: false,
      assignmentTasksLoading: false,
      assignmentTasks: [],
      selectedAssignmentTitle: '',
      // Grant-extra-attempts modal (assessments only)
      showGrantAttemptsModal: false,
      grantAttemptsRow: null,
      grantAttemptsCount: 1,
      grantAttemptsReason: '',
      grantAttemptsSubmitting: false,
      currentStudentId: null,
      currentCourseId: null,
      currentCourseName: null,
      libraryVideos: [],
      libraryVideosLoading: false,
      libraryVideosPerPage: 15,
      libraryVideosCurrentPage: 1,
      libraryVideosTotal: 0,
      lectureVideos: [],
      lectureVideosLoading: false,
      lectureVideosLoaded: false,
      chapterProgressList: [],
      chapterProgressFinalExam: null,
      chapterProgressRestriction: null,
      chapterProgressLoading: false,
      chapterProgressLoaded: false,
      submissionsData: { assignments: [], quizzes: [], sections: [] },
      submissionsLoading: false,
      submissionsLoaded: false,
      submissionsStatusFilter: 'All',
      submissionsTypeFilter: 'All',
      submissionsApiFilter: null,
      overallProgressSummary: null,
      overallProgressLoading: false,
      exportingExcel: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },
    // Super admins only ever get the grant control, so the Action column is
    // dead weight unless some visible row can actually be granted attempts.
    showActionColumn() {
      if (!this.isSuperAdmin) return true;
      return this.submissionsRows.some((row) => this.canGrantAttempts(row));
    },
    primaryColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color', '#1a1a1a');
    },
    secondaryColor() {
      return get(this.getLoggedInUser, 'linked_entity.secondary_color', '#6b7280');
    },
    studentDetails() {
      return get(this.transcript, 'student', {});
    },
    studentFullName() {
      const first = get(this.studentDetails, 'first_name', '');
      const last = get(this.studentDetails, 'last_name', '');
      return `${first} ${last}`.trim() || '-';
    },
    finalExam() {
      return get(this.transcript, 'final_exam', null);
    },
    // Index of the chapter the student resumes from ("continue here"). Only
    // meaningful when the course enforces sequential access — without it the
    // student can open any chapter, so there is nothing to point at or lock.
    // -1 when there is nothing pending or the pointer chapter is not in the list.
    continueChapterIndex() {
      const restriction = this.chapterProgressRestriction;
      if (
        !restriction
        || !restriction.restrict_next_lesson
        || !restriction.show_current
        || !restriction.chapter_id
      ) {
        return -1;
      }
      return this.chapterProgressList.findIndex(
        (chapter) => String(chapter.id) === String(restriction.chapter_id),
      );
    },
    overallProgressLabel() {
      const value = get(this.transcript, 'overall_progress');
      return value !== undefined && value !== null ? `${this.formatPercent(value)}%` : '-';
    },
    overallGradeLabel() {
      const value = get(this.transcript, 'overall_course_grade');
      return value !== undefined && value !== null ? `${value}%` : '—';
    },
    // Overall Grade only covers graded work the student has finished, so say
    // how much of the course's grade weight that is.
    overallGradeCaption() {
      const value = get(this.transcript, 'overall_course_grade');
      if (value === undefined || value === null) {
        return 'No graded work yet';
      }
      const weight = Number(get(this.transcript, 'graded_weight_completed'));
      if (!Number.isFinite(weight)) {
        return 'on completed graded work';
      }
      return `on completed graded work (${weight.toFixed(0)}% of course weight)`;
    },
    enrollmentStatusLabel() {
      return this.formatStatus(get(this.transcript, 'enrollment_status'));
    },
    gradeChipClass() {
      const grade = get(this.transcript, 'grade');
      if (grade === 'passed') {
        return 'grade-passed';
      } else if (grade === 'failed') {
        return 'grade-failed';
      }
      return '-';
    },
    overallProgressPercentage() {
      const value = this.get(this.transcript, 'overall_progress');
      const numeric = Number(value);
      const safeValue = Number.isFinite(numeric) ? numeric : 0;
      return `${safeValue.toFixed(0)}%`;
    },
    overallProgressDescription() {
      const lastLoginAt = this.get(this.overallProgressSummary, 'student_lms_last_login_at');
      if (!lastLoginAt) {
        return 'Keep the momentum going!';
      }
      return `LMS last login: ${this.formatDateTime(lastLoginAt)}`;
    },
    // --- State compliance seat time (CA CNA / CDPH E276) -------------------
    // Gated on required_theory_hours OR enforce_read_time, not on
    // enforce_read_time alone: a school may want hours reported without
    // blocking lesson completion, and keying only on the enforcement flag
    // would hide the report in exactly that case.
    showComplianceSeatTime() {
      const required = Number(this.get(this.transcript, 'required_theory_hours'));
      const enforced = this.get(this.transcript, 'enforce_read_time');
      return Boolean(enforced) || (Number.isFinite(required) && required > 0);
    },
    requiredTheoryHours() {
      const value = Number(this.get(this.transcript, 'required_theory_hours'));
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    minutesPerCreditHour() {
      const value = Number(this.get(this.transcript, 'minutes_per_credit_hour'));
      return Number.isFinite(value) && value > 0 ? value : 50;
    },
    theoryHoursEarned() {
      const value = Number(this.get(this.transcript, 'credit_hours_earned'));
      // The API returns null when the divisor is missing; show a dash rather
      // than a misleading zero.
      return Number.isFinite(value) ? value.toFixed(1) : '—';
    },
    theoryHoursPercentage() {
      const earned = Number(this.get(this.transcript, 'credit_hours_earned'));
      const required = this.requiredTheoryHours;
      if (!required || !Number.isFinite(earned)) {
        return '0%';
      }
      return `${Math.min(100, (earned / required) * 100).toFixed(0)}%`;
    },
    theoryHoursComplete() {
      const earned = Number(this.get(this.transcript, 'credit_hours_earned'));
      const required = this.requiredTheoryHours;
      return Boolean(required) && Number.isFinite(earned) && earned >= required;
    },
    formattedReadTime() {
      const minutes = Number(this.get(this.transcript, 'total_read_minutes'));
      return Number.isFinite(minutes) && minutes > 0
        ? formatMinutesToHoursAndMinutes(minutes)
        : '0 min';
    },
    formattedWatchedTime() {
      const minutes = Number(this.get(this.transcript, 'total_watched_minutes'));
      return Number.isFinite(minutes) && minutes > 0
        ? formatMinutesToHoursAndMinutes(minutes)
        : '0 min';
    },
    formattedEstimatedTime() {
      const minutes = Number(this.get(this.transcript, 'total_time_spent_minutes'));
      if (!Number.isFinite(minutes) || minutes <= 0) {
        return '';
      }
      if (minutes < 60) {
        return `${minutes} min`;
      }
      const hours = Math.floor(minutes / 60);
      const remaining = minutes % 60;
      if (remaining === 0) {
        return `${hours} hr`;
      }
      return `${hours} hr ${remaining} min`;
    },
    activityTabs() {
      
      return [
        {
          key: 'progress',
          label: 'Progress',
          icon: 'fas fa-chart-line',
          
        },
        {
          key: 'submissions',
          label: 'Submissions',
          icon: 'fas fa-file-alt',
        },
        {
          key: 'videos',
          label: 'Lecture Videos',
          icon: 'fas fa-play-circle',
        },
        {
          key: 'library_videos',
          label: 'Skill Videos',
          icon: 'fas fa-graduation-cap',
        },
      ];
    },
    
    isVideoTab() {
      return this.activeTab === 'videos' || this.activeTab === 'library_videos';
    },
    showChapterTable() {
      return this.activeTab === 'progress';
    },
    showSubmissionsTable() {
      return this.activeTab === 'submissions';
    },
    activeTabMeta() {
      const active = this.activityTabs.find((tab) => tab.key === this.activeTab);
      return active && active.meta ? active.meta : null;
    },
    allSubmissionsRows() {
      const sections = this.get(this.submissionsData, 'sections', []);
      if (Array.isArray(sections) && sections.length > 0) {
        return sections.reduce((acc, section, sectionIndex) => {
          const sectionItems = Array.isArray(section.items) ? section.items : [];
          sectionItems.forEach((item, itemIndex) => {
            const sectionOrder = Number(section.section_order);
            acc.push({
              ...item,
              section_id: item.section_id || section.section_id || `section-${sectionIndex}`,
              section_title: item.section_title || section.section_title || 'Untitled Lecture',
              section_order: Number.isFinite(sectionOrder) ? sectionOrder : sectionIndex,
              submissionType: this.resolveSubmissionType(item),
              _rowIndex: `${sectionIndex}-${itemIndex}`,
            });
          });
          return acc;
        }, []);
      }

      const assignments = this.get(this.submissionsData, 'assignments', []).map((item, index) => ({
        ...item,
        section_id: item.section_id || item.chapter_id || `assignment-section-${index}`,
        section_title: item.section_title || item.chapter_title || 'Course Assessments',
        section_order: Number.isFinite(Number(item.chapter_order)) ? Number(item.chapter_order) : 1000000000,
        submissionType: 'Assignment',
        _rowIndex: `a-${index}`,
      }));
      const quizzes = this.get(this.submissionsData, 'quizzes', []).map((item, index) => ({
        ...item,
        section_id: item.section_id || item.chapter_id || `quiz-section-${index}`,
        section_title: item.section_title || item.chapter_title || 'Course Assessments',
        section_order: Number.isFinite(Number(item.chapter_order)) ? Number(item.chapter_order) : 1000000000,
        submissionType: 'Assessment',
        _rowIndex: `q-${index}`,
      }));
      return [...assignments, ...quizzes];
    },
    submissionsRows() {
      let rows = [...this.allSubmissionsRows];
      
      // Apply status filter
      if (this.submissionsStatusFilter !== 'All') {
        rows = rows.filter(row => {
          const status = String(row.status || '').toLowerCase();
          return status === this.submissionsStatusFilter.toLowerCase();
        });
      }
      
      // Apply type filter
      if (this.submissionsTypeFilter !== 'All') {
        rows = rows.filter(row => row.submissionType === this.submissionsTypeFilter);
      }

      // Apply graded/mock filter (assessments only)
      if (this.submissionsApiFilter === 'graded') {
        rows = rows.filter(row => row.submissionType !== 'Assessment' || (row.weight > 0 && !row.is_mock_exam));
      } else if (this.submissionsApiFilter === 'mock_exam') {
        rows = rows.filter(row => row.submissionType !== 'Assessment' || row.is_mock_exam);
      }
      
      return rows;
    },
    submissionsSectionRows() {
      const grouped = this.submissionsRows.reduce((acc, row) => {
        const sectionId = row.section_id || row.chapter_id || 'course_assessments';
        if (!acc[sectionId]) {
          acc[sectionId] = {
            sectionId,
            sectionTitle: row.section_title || row.chapter_title || 'Course Assessments',
            sectionOrder: Number.isFinite(Number(row.section_order)) ? Number(row.section_order) : 1000000000,
            items: [],
          };
        }
        acc[sectionId].items.push(row);
        return acc;
      }, {});

      return Object.values(grouped)
        .sort((a, b) => a.sectionOrder - b.sectionOrder)
        .map((section) => ({
          ...section,
          items: section.items.sort((a, b) => {
            const aLesson = Number(a.lesson_order);
            const bLesson = Number(b.lesson_order);
            const aLessonOrder = Number.isFinite(aLesson) ? aLesson : 1000000000;
            const bLessonOrder = Number.isFinite(bLesson) ? bLesson : 1000000000;
            if (aLessonOrder !== bLessonOrder) {
              return aLessonOrder - bLessonOrder;
            }
            return String(a.title || '').localeCompare(String(b.title || ''));
          }),
        }));
    },
    submissionsStatusOptions() {
      return ['All', 'Completed', 'Pending'];
    },
    submissionsTypeOptions() {
      return ['All', 'Assignment', 'Assessment'];
    },
    submissionsApiFilterOptions() {
      return [
        { value: null, text: 'All' },
        { value: 'graded', text: 'Graded' },
        { value: 'mock_exam', text: 'Mock Exam' },
      ];
    },
    videoRows() {
      if (this.activeTab === 'videos') {
        return this.lectureVideos;
      } else if (this.activeTab === 'library_videos') {
        return this.libraryVideos;
      }
      return [];
    },
  },
  methods: {
    ...mapActions('courseEnrollments', ['getAcademicTranscript', 'getCourseEnrollment']),
    ...mapActions('skills', ['getStudentWatchedVideos']),
    ...mapActions('courses', ['grantExtraQuizAttempts']),
    async ensureChapterProgressData() {
      if (!this.showChapterTable || this.chapterProgressLoaded || this.chapterProgressLoading) {
        return;
      }
      await Promise.all([
        this.fetchChapterProgressList(),
        this.fetchOverallProgressSummary()
      ]);
    },
    async fetchOverallProgressSummary() {
      if (!this.currentCourseId || !this.currentStudentId) return;

      this.overallProgressLoading = true;
      try {
        const response = await axios.get('api/lms/progress-summary/overall-progress/', {
          params: { course_id: this.currentCourseId, student_id: this.currentStudentId },
        });
        const payload = response.data?.data || response.data || {};
        this.overallProgressSummary = payload;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load progress summary.' });
      } finally {
        this.overallProgressLoading = false;
      }
    },
    async fetchChapterProgressList() {
      if (!this.currentCourseId || !this.currentStudentId) return;

      this.chapterProgressLoading = true;
      try {
        const response = await axios.get('api/lms/progress-summary/chapter-progress-list/', {
          params: { course_id: this.currentCourseId, student_id: this.currentStudentId },
        });
        const payload = response.data?.data || response.data || {};
        this.chapterProgressList = Array.isArray(payload.chapters) ? payload.chapters : [];
        this.chapterProgressFinalExam = payload.final_exam || null;
        this.chapterProgressRestriction = payload.restriction || null;
        this.chapterProgressLoaded = true;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load chapter progress.' });
      } finally {
        this.chapterProgressLoading = false;
      }
    },
    async ensureSubmissionsData() {
      if (!this.showSubmissionsTable || this.submissionsLoaded || this.submissionsLoading) {
        return;
      }
      await this.fetchSubmissionsData();
    },
    async ensureLectureVideosData() {
      if (this.activeTab !== 'videos' || this.lectureVideosLoaded || this.lectureVideosLoading) {
        return;
      }
      await this.fetchLectureVideos();
    },
    async fetchSubmissionsData() {
      if (!this.currentCourseId || !this.currentStudentId) return;

      this.submissionsLoading = true;
      try {
        const params = {
          student_id: this.currentStudentId,
        };
        const response = await axios.get(`api/lms/submissions/course/${this.currentCourseId}/`, {
          params,
        });
        const payload = response.data?.data || response.data || {};
        this.submissionsData = {
          assignments: Array.isArray(payload.assignments) ? payload.assignments : [],
          quizzes: Array.isArray(payload.quizzes) ? payload.quizzes : [],
          sections: Array.isArray(payload.sections) ? payload.sections : [],
        };
        this.submissionsLoaded = true;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load submissions.' });
      } finally {
        this.submissionsLoading = false;
      }
    },
    async fetchLectureVideos() {
      if (!this.currentCourseId || !this.currentStudentId) return;

      this.lectureVideosLoading = true;
      try {
        const response = await axios.get(`api/lms/submissions/course/${this.currentCourseId}/videos/`, {
          params: { student_id: this.currentStudentId },
        });
        const payload = response.data?.data || response.data || {};
        this.lectureVideos = Array.isArray(payload.videos) ? payload.videos : [];
        this.lectureVideosLoaded = true;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load lecture videos.' });
      } finally {
        this.lectureVideosLoading = false;
      }
    },
    // --- Grant extra assessment attempts -------------------------------- //

    canGrantAttempts(row) {
      // Assessments only. Assignments have their own attempt handling and
      // mock exams are unlimited practice, so neither is offered here.
      if (!row || row.submissionType !== 'Assessment') return false;
      if (row.is_mock_exam) return false;
      // Only meaningful once the student has actually run out.
      return Boolean(row.attempts_exhausted);
    },

    // Super admins only get the grant control, so their rows carry actions
    // only when there is something to grant.
    showRowActions(row) {
      if (!row || !row.status || row.status.toLowerCase() !== 'completed') return false;
      return this.isSuperAdmin ? this.canGrantAttempts(row) : true;
    },

    grantAttemptsTooltip(row) {
      const used = this.get(row, 'attempts_count', 0);
      const limit = (this.get(row, 'max_attempts', 0) || 0) + (this.get(row, 'extra_attempts_granted', 0) || 0);
      return `Used ${used} of ${limit} attempts. Add more so the student can retry.`;
    },

    openGrantAttempts(row) {
      this.grantAttemptsRow = row;
      this.grantAttemptsCount = 1;
      this.grantAttemptsReason = '';
      this.showGrantAttemptsModal = true;
    },

    async submitGrantAttempts() {
      const row = this.grantAttemptsRow;
      if (!row) return;

      const count = Number(this.grantAttemptsCount);
      if (!count || count < 1 || count > 5) {
        this.makeToast({ variant: 'warning', msg: 'Enter between 1 and 5 additional attempts.' });
        return;
      }

      const quizId = this.get(row, 'quiz_id') || this.get(row, 'id');
      const studentId = this.currentStudentId;
      if (!quizId || !studentId) {
        this.makeToast({ variant: 'warning', msg: 'Missing student or assessment identifier.' });
        return;
      }

      // A quiz can be reused across lessons, so the attempt record must be
      // matched at the level it was taken.
      const data = {
        student_id: studentId,
        quiz_id: quizId,
        additional_attempts: count,
        reason: this.grantAttemptsReason || undefined,
      };
      const quizType = this.get(row, 'quiz_type');
      if (quizType === 'lesson' && this.get(row, 'lesson_id')) {
        data.lesson_id = this.get(row, 'lesson_id');
      } else if (quizType === 'chapter' && this.get(row, 'chapter_id')) {
        data.chapter_id = this.get(row, 'chapter_id');
      } else {
        data.course_id = this.get(row, 'course_id') || this.currentCourseId;
      }

      this.grantAttemptsSubmitting = true;
      try {
        const res = await this.grantExtraQuizAttempts(data);
        const remaining = this.get(res, 'data.attempts_remaining');
        this.makeToast({
          variant: 'success',
          msg:
            remaining === null || remaining === undefined
              ? 'Attempts added.'
              : `Attempts added. The student now has ${remaining} remaining.`,
        });
        this.showGrantAttemptsModal = false;
        this.grantAttemptsRow = null;
        // Refresh so the row reflects the new ceiling.
        await this.fetchSubmissionsData();
      } catch (err) {
        this.makeToast({
          variant: 'danger',
          msg: this.get(err, 'response.data.message') || this.$t('generalMsgs.genErrorMsg'),
        });
      }
      this.grantAttemptsSubmitting = false;
    },

    openSubmissionAttempts(row) {
      const enrollmentId = this.$route.params.enrollmentId;
      const studentId = this.currentStudentId;
      
      if (!enrollmentId || !studentId) {
        this.makeToast({ variant: 'warning', msg: 'Missing identifiers to open attempts.' });
        return;
      }

      if (row.submissionType === 'Assignment') {
        const assignmentId = this.get(row, 'assignment_id') || this.get(row, 'id');
        const lessonId = this.get(row, 'lesson_id');
        
        if (!assignmentId) {
          this.makeToast({ variant: 'warning', msg: 'Missing assignment identifier.' });
          return;
        }
        
        const query = { 
          student_id: String(studentId)
        };
        if (lessonId) {
          query.lesson_id = lessonId;
        }
        
        this.$router.push({
          name: 'assignment-attempts',
          params: { enrollmentId, assignmentId },
          query
        });
      } else if (row.submissionType === 'Assessment' || row.submissionType === 'Quiz') {
        const quizId = this.get(row, 'quiz_id') || this.get(row, 'id');
        
        if (!quizId) {
          this.makeToast({ variant: 'warning', msg: 'Missing quiz identifier.' });
          return;
        }
        
        const query = { 
          student_id: String(studentId)
        };
        
        // Pass appropriate ID based on quiz_type
        const quizType = this.get(row, 'quiz_type');
        if (quizType === 'lesson') {
          const lessonId = this.get(row, 'lesson_id');
          if (lessonId) {
            query.lesson_id = lessonId;
          }
        } else if (quizType === 'chapter') {
          const chapterId = this.get(row, 'chapter_id');
          if (chapterId) {
            query.chapter_id = chapterId;
          }
        } else if (quizType === 'final') {
          const courseId = this.get(row, 'course_id') || this.currentCourseId;
          if (courseId) {
            query.course_id = courseId;
          }
        }
        
        this.$router.push({
          name: 'quiz-attempts',
          params: { enrollmentId, quizId },
          query
        });
      }
    },
    formatEstimatedTime(minutes) {
      if (!minutes || !Number.isFinite(Number(minutes))) {
        return '-';
      }
      const mins = Number(minutes);
      if (mins < 60) {
        return `${mins}m`;
      }
      const hours = Math.floor(mins / 60);
      const remaining = mins % 60;
      if (remaining === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remaining}m`;
    },
    isQuiz(activity) {
      const type = String(this.get(activity, 'type') || '').toLowerCase();
      return type === 'quiz' || type === 'chapter_quiz';
    },
    isAssignmentCompleted(activity) {
      const type = String(this.get(activity, 'type') || '').toLowerCase();
      const status = String(this.get(activity, 'status') || '').toLowerCase();
      return type === 'assignment' && status === 'completed';
    },
    handleActivityClick(activity) {
      if (this.isAssignmentCompleted(activity)) {
        this.openAssignmentAttempts(activity);
      } else if (this.isQuiz(activity)) {
        this.openQuizAttempts(activity);
      }
    },
    openQuizAttempts(activity) {
      const enrollmentId = this.$route.params.enrollmentId;
      const quizId = this.get(activity, 'quiz_id') || this.get(activity, 'id');
      const studentId = this.currentStudentId;
      if (!quizId || !enrollmentId || !studentId) {
        this.makeToast({ variant: 'warning', msg: 'Missing identifiers to open attempts.' });
        return;
      }
      
      const query = { student_id: String(studentId) };
      
      // Pass appropriate ID based on quiz_type
      const quizType = this.get(activity, 'quiz_type');
      if (quizType === 'lesson') {
        const lessonId = this.get(activity, 'lesson_id');
        if (lessonId) {
          query.lesson_id = lessonId;
        }
      } else if (quizType === 'chapter') {
        const chapterId = this.get(activity, 'chapter_id');
        if (chapterId) {
          query.chapter_id = chapterId;
        }
      } else if (quizType === 'final') {
        const courseId = this.get(activity, 'course_id') || this.currentCourseId;
        if (courseId) {
          query.course_id = courseId;
        }
      }
      
      this.$router.push({
        name: 'quiz-attempts',
        params: { enrollmentId, quizId },
        query
      });
    },
    openAssignmentAttempts(activity) {
      const enrollmentId = this.$route.params.enrollmentId;
      const assignmentId = this.get(activity, 'assignment_id') || this.get(activity, 'id');
      const lessonId = this.get(activity, 'lesson_id');
      const studentId = this.currentStudentId;
      
      if (!assignmentId || !enrollmentId || !studentId) {
        this.makeToast({ variant: 'warning', msg: 'Missing identifiers to open attempts.' });
        return;
      }
      
      this.$router.push({
        name: 'assignment-attempts',
        params: { enrollmentId, assignmentId },
        query: { 
          student_id: String(studentId),
          lesson_id: lessonId || undefined
        }
      });
    },
    async fetchLibraryVideos(studentId) {
      if (!studentId) return;
      // Guard here rather than only at the call sites: the activeTab watcher
      // and switchToLibraryVideosTab both reach this, and the watcher runs
      // synchronously on assignment -- before any caller-side flag is set.
      if (this.libraryVideosLoading) return;

      this.libraryVideosLoading = true;
      try {
        const limit = this.libraryVideosPerPage;
        const offset = (this.libraryVideosCurrentPage - 1) * this.libraryVideosPerPage;
        const params = { student_id: studentId, limit, offset, pagination: true };
        if (this.currentCourseId) {
          params.course_id = this.currentCourseId;
        }
        const response = await this.getStudentWatchedVideos(params);

        // Support both paginated { data: { count, results } } and list responses
        const payload = response.data || response;
        const count = this.get(payload, 'count');
        const results = this.get(payload, 'results');

        if (Array.isArray(results)) {
          this.libraryVideos = results;
          this.libraryVideosTotal = Number.isFinite(count) ? count : results.length;
        } else if (Array.isArray(payload)) {
          this.libraryVideos = payload;
          this.libraryVideosTotal = payload.length;
        } else {
          // Single object fallback
          this.libraryVideos = payload ? [payload] : [];
          this.libraryVideosTotal = this.libraryVideos.length;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load library videos.' });
        this.libraryVideos = [];
        this.libraryVideosTotal = 0;
      } finally {
        this.libraryVideosLoading = false;
      }
    },
    onLibraryVideosPageChange(pageNum) {
      this.libraryVideosCurrentPage = pageNum;
      if (this.currentStudentId) {
        this.fetchLibraryVideos(this.currentStudentId);
      }
    },
    async switchToLibraryVideosTab() {
      this.activeTab = 'library_videos';
      
      // Update query parameter
      if (this.$route.query.tab !== 'library_videos') {
        this.$router.replace({ 
          query: { ...this.$route.query, tab: 'library_videos' } 
        }).catch(() => {});
      }
      
      // reset to first page when switching into the tab
      this.libraryVideosCurrentPage = 1;
      // Fetch library videos if not already loaded
      if (this.libraryVideos.length === 0 && !this.libraryVideosLoading && this.currentStudentId) {
        await this.fetchLibraryVideos(this.currentStudentId);
      }
    },
    get,
    handleTabClick(tabKey) {
      if (tabKey === 'library_videos') {
        this.switchToLibraryVideosTab();
        return;
      }
      this.activeTab = tabKey;
      
      // Update query parameter
      if (this.$route.query.tab !== tabKey) {
        this.$router.replace({ 
          query: { ...this.$route.query, tab: tabKey } 
        }).catch(() => {});
      }
      
      if (tabKey === 'progress') {
        this.ensureChapterProgressData();
      } else if (tabKey === 'submissions') {
        this.ensureSubmissionsData();
      } else if (tabKey === 'videos') {
        this.ensureLectureVideosData();
      }
    },
    formatStatus(value) {
      if (!value) return '-';

      const map = {
        enrolled: 'Enrolled',
        completed: 'Completed',
        in_progress: 'In Progress',
        not_started: 'Not Started',
        dropped: 'Dropped',
        passed: 'Passed',
        failed: 'Failed',
        pending: 'Pending',
        grading: 'Grading',
      };

      const normalized = String(value).toLowerCase();
      if (map[normalized]) {
        return map[normalized];
      }

      return startCase(normalized.replace(/[-_]/g, ' '));
    },
    async fetchTranscript() {
      const { enrollmentId } = this.$route.params;
      if (!enrollmentId) {
        this.makeToast({ variant: 'danger', msg: 'Missing enrollment information.' });
        return;
      }

      this.isLoading = true;
      try {
        const enrollmentResponse = await this.getCourseEnrollment(enrollmentId);
        const enrollment = enrollmentResponse.data || enrollmentResponse;
        const courseId = get(enrollment, 'course.id') || get(enrollment, 'course_id');
        const studentId = get(enrollment, 'student.id') || get(enrollment, 'student_id');

        if (!courseId || !studentId) {
          this.makeToast({ variant: 'danger', msg: 'Enrollment is missing course or student data.' });
          this.isLoading = false;
          return;
        }

        // Paint the identity header from the enrollment while the transcript
        // is still in flight -- it carries the same student object, so the
        // avatar/name/email block renders a full round-trip earlier instead of
        // sitting blank. Overwritten wholesale by the transcript below.
        const enrolledStudent = get(enrollment, 'student');
        if (enrolledStudent) {
          this.transcript = { student: enrolledStudent };
        }

        const response = await this.getAcademicTranscript({
          course_id: courseId,
          student_id: studentId,
        });
        this.transcript = response.data || {};
        this.currentStudentId = studentId;
        this.currentCourseId = courseId;
        this.currentCourseName = get(enrollment, 'course.title') || get(enrollment, 'course.name') || null;

        // reset chapter progress cache
        this.chapterProgressList = [];
        this.chapterProgressFinalExam = null;
        this.chapterProgressRestriction = null;
        this.chapterProgressLoaded = false;
        this.submissionsData = { assignments: [], quizzes: [], sections: [] };
        this.submissionsLoaded = false;
        this.lectureVideos = [];
        this.lectureVideosLoaded = false;

        // Release the page overlay here rather than after the tab data.
        // The header, summary chips and progress card are fully populated by
        // this point, so blocking on a second round-trip only shows the user a
        // blank page for longer. The active tab renders its own spinner while
        // its data streams in underneath.
        this.isLoading = false;

        // Deliberately not awaited: the tab owns its loading state, and the
        // catch below is for the transcript, not for tab data (which reports
        // its own failures).
        this.loadActiveTabData();
        return;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load academic transcript.' });
      }
      this.isLoading = false;
    },
    /**
     * Fetch whichever tab is showing. Each ensure* call is already guarded
     * against duplicate and repeat fetches, so this is safe to call again.
     */
    loadActiveTabData() {
      if (this.activeTab === 'progress') {
        return this.ensureChapterProgressData();
      }
      if (this.activeTab === 'submissions') {
        return this.ensureSubmissionsData();
      }
      if (this.activeTab === 'videos') {
        return this.ensureLectureVideosData();
      }
      if (this.activeTab === 'library_videos') {
        // fetchLibraryVideos has no in-flight guard of its own, so respect the
        // same "already loaded / already loading" check switchToLibraryVideosTab
        // uses. Without this, clicking the tab fires the request twice.
        if (
          this.libraryVideos.length === 0
          && !this.libraryVideosLoading
          && this.currentStudentId
        ) {
          return this.fetchLibraryVideos(this.currentStudentId);
        }
      }
      return Promise.resolve();
    },
    formatDate(value) {
      if (!value) return '-';
      try {
        return formatShortDate(value);
      } catch (e) {
        return value;
      }
    },
    formatDateTime(value) {
      if (!value) return '-';
      try {
        return formatDateTime(value);
      } catch (e) {
        return value;
      }
    },
    formatPercent(percent) {
      if (percent === null || percent === undefined || percent === '') return '0.00';
      const number = Number(percent);
      if (!Number.isFinite(number)) return '0.00';
      return number.toFixed(2);
    },
    
    getActivityIcon(type) {
      const iconMap = {
        'lecture': 'fas fa-play-circle',
        'quiz': 'fas fa-question-circle',
        'assignment': 'fas fa-file-alt',
        'chapter_quiz': 'fas fa-clipboard-check',
        'library_video': 'fas fa-video',
      };
      return iconMap[type] || 'fas fa-circle';
    },
    cleanIndexedLabel(value) {
      if (value === null || value === undefined) {
        return value;
      }
      const cleaned = String(value).replace(/^\s*\d+(?:\.\d+)*(?:\s*[–—-])?\s*/, '');
      return cleaned.trim();
    },
    activityCompletedDisplay(activity) {
      const completedAt = this.get(activity, 'completed_at');
      if (completedAt) {
        return this.formatDateTime(completedAt);
      }
      const watched = this.get(activity, 'last_watched_at');
      if (watched) {
        return this.formatDateTime(watched);
      }
      return '';
    },
    formatWeightValue(weight) {
      if (weight === null || weight === undefined || weight === '') {
        return '-';
      }
      const numeric = Number(weight);
      if (Number.isFinite(numeric)) {
        return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 2)}%`;
      }
      return weight;
    },
    formatChapterLessons(chapter) {
      const completed = this.get(chapter, 'completed_lessons', 0);
      const total = this.get(chapter, 'total_lessons', 0);
      return `${completed}/${total}`;
    },
    isChapterLocked(chapter, index) {
      if (this.continueChapterIndex < 0 || index <= this.continueChapterIndex) {
        return false;
      }
      return Number(this.get(chapter, 'progress_percent', 0)) < 100;
    },
    formatEstimatedMinutes(minutes) {
      if (!Number.isFinite(Number(minutes))) {
        return '-';
      }
      return formatMinutesToHoursAndMinutes(Number(minutes));
    },
    async exportSubmissionsToExcel() {
      if (this.exportingExcel || this.submissionsRows.length === 0) return;

      this.exportingExcel = true;
      try {
        const courseName = this.currentCourseName || get(this.transcript, 'course.title') || get(this.transcript, 'course_name') || '-';
        const studentName = this.studentFullName;

        const rows = this.submissionsRows.map((row) => ({
          title: row.title || '-',
          status: this.formatStatus(row.status),
          status_date: row.status && row.status.toLowerCase() === 'completed' && row.completed
            ? this.formatDateTime(row.completed)
            : '-',
          weight: this.formatWeightValue(row.weight),
          score: this.formatSubmissionScore(row),
          estimated_time: this.formatEstimatedTime(row.estimated_time_minutes),
        }));

        const response = await axios.post(
          'api/lms/submissions/export/',
          { course_name: courseName, student_name: studentName, rows },
        );

        const fileUrl = get(response, 'data.data.file_url') || get(response, 'data.file_url');
        if (!fileUrl) {
          this.makeToast({ variant: 'danger', msg: 'No file URL returned from server.' });
          return;
        }

        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `${studentName}_submissions.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to export submissions.' });
      } finally {
        this.exportingExcel = false;
      }
    },
    formatSubmissionScore(row) {
      const score = this.get(row, 'score');
      if (score === null || score === undefined || score === '') {
        return '-';
      }
      const numeric = Number(score);
      return Number.isFinite(numeric) ? `${this.formatPercent(numeric)}%` : score;
    },
    resolveSubmissionType(item) {
      const category = String(item?.item_category || '').toLowerCase();
      if (category === 'assignment') {
        return 'Assignment';
      }
      if (category === 'quiz') {
        return 'Assessment';
      }
      return item?.assignment_id ? 'Assignment' : 'Assessment';
    },
  },
  async mounted() {
    // Initialize activeTab from query parameter if present
    const tabFromQuery = this.$route.query.tab;
    const validTabs = ['progress', 'submissions', 'videos', 'library_videos'];
    if (tabFromQuery && validTabs.includes(tabFromQuery)) {
      this.activeTab = tabFromQuery;
    }
    
    await this.fetchTranscript();
  },
  watch: {
    // One entry point for every tab, so switching always fetches lazily on
    // first view. Previously only 'submissions' was handled here and the other
    // tabs depended on the initial fetchTranscript chain, which no longer
    // awaits them.
    activeTab() {
      this.loadActiveTabData();
    },
    '$route.params.enrollmentId'(val, oldVal) {
      if (val && val !== oldVal) {
        this.fetchTranscript();
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Remove outer card border and styling
::v-deep .card {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

::v-deep .card-header {
  background: #ffffff !important;
  border: 1px solid #e5e7f1 !important;
  border-radius: 1rem !important;
  padding: 1.5rem !important;
  margin-bottom: 1.5rem !important;
  box-shadow: 0 2px 8px rgba(15, 33, 55, 0.04) !important;
}

::v-deep .card-body {
  padding: 0 !important;
}

.gradebook-header {
  padding-right: 0.5rem;
}

.header-progress .h2 {
  color: #2f4f75;
  font-size: 1.75rem;
}

.gradebook-surface {
  background: transparent;
  border-radius: 0;
  padding: 0;
  max-width: 100%;
}

.profile-card {
  background: #ffffff;
  border: 1px solid #e5e7f1;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(15, 33, 55, 0.04);
}

.summary-identification {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.summary-avatar {
  border: 3px solid rgba(47, 79, 117, 0.12);
  border-radius: 50%;
}

.summary-info {
  text-align: center;
  margin-bottom: 1.5rem;
}

.summary-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;
}

.summary-email {
  color: #6b7280;
  font-size: 0.85rem;
}

.summary-meta-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.85rem;
}

.summary-chip {
  padding: 0.8rem 1rem;
  text-align: left;
  border-radius: 0.75rem;
  border: 1px solid #e3e8ef;
  background: #f9fbff;

  &.grade-passed {
    border-color: #10b981;
    background: #ecfdf5;
  }

  &.grade-failed {
    border-color: #ef4444;
    background: #fef2f2;
  }
}

.chip-label {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  margin-bottom: 0.25rem;
}

.chip-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
}

.overall-progress-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
}

.overall-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 10px;
}

.overall-progress-header .label {
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #94a3b8;
  margin-bottom: 0.25rem;
}

.overall-progress-header .value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
}

.overall-progress-header .caption {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0;
}

.grade-pill {
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0.75rem 1.25rem;
  display: flex;
  flex-direction: column;
  text-align: right;
  font-size: 0.8rem;
  color: #475569;

  strong {
    font-size: 1.1rem;
    color: #0f172a;
  }
}

.overall-progress-bar {
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: #edf2ff;
  margin: 0.25rem 0 0.75rem;
  overflow: hidden;
}

.overall-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #60a5fa, #2563eb);
  border-radius: inherit;
  transition: width 0.3s ease;
}

.overall-progress-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #64748b;
}

/* Loading placeholders. Shaped like the rows they stand in for, so content
   landing does not shift the layout. */
.skeleton-block {
  padding: 0.5rem 0;
}

.skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.skeleton-row:last-child {
  border-bottom: none;
}

.skeleton-line {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-line--title {
  flex: 1 1 auto;
  max-width: 45%;
}

.skeleton-line--meta {
  flex: 0 0 90px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

/* Respect users who ask for less motion; the placeholder still reads as
   pending without the shimmer. */
@media (prefers-reduced-motion: reduce) {
  .skeleton-line {
    animation: none;
  }
}

/* State compliance seat time. Only rendered for opted-in courses. */
.compliance-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #0ea5e9;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
}

.compliance-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.compliance-header .label {
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #94a3b8;
  margin-bottom: 0.25rem;
}

.compliance-header .value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
  line-height: 1.1;
}

.compliance-header .value-target {
  font-size: 1.25rem;
  font-weight: 600;
  color: #64748b;
}

.compliance-header .value-unit {
  font-size: 0.9rem;
  font-weight: 500;
  color: #94a3b8;
  margin-left: 0.25rem;
}

/* The credit-hour basis sits beside the number: 42.5 hrs looks wrong until
   you know an hour is 50 minutes here, not 60. */
.basis-pill {
  display: inline-block;
  background: #f0f9ff;
  color: #0369a1;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.compliance-progress-bar {
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: #e0f2fe;
  margin: 0.75rem 0;
  overflow: hidden;
}

.compliance-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #38bdf8, #0284c7);
  border-radius: inherit;
  transition: width 0.3s ease;
}

.compliance-progress-fill.complete {
  background: linear-gradient(90deg, #4ade80, #16a34a);
}

.compliance-split {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.split-item {
  flex: 1 1 140px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.6rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.split-item--muted {
  background: #ffffff;
  border-style: dashed;
}

.split-label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.split-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.compliance-note {
  margin: 0.75rem 0 0;
  font-size: 0.75rem;
  color: #94a3b8;
  line-height: 1.4;
}

.chapter-progress-section {
  margin-top: 1rem;
}

.chapter-empty-state {
  background: #fff;
  border: 1px dashed #cbd5f5;
  border-radius: 1rem;
  padding: 1.5rem;
  text-align: center;
  color: #94a3b8;
}

.section-header-with-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.section-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  gap: 0.5rem;
  color: #0f172a;
}

.estimated-time-badge {
  background: #dbeafe;
  color: #1e40af;
  border-radius: 999px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  
  i {
    font-size: 0.9rem;
  }
}

// Consolidated Progress Card
.consolidated-progress-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(15, 33, 55, 0.04);
}

.consolidated-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0f172a;
  
  i {
    color: #2563eb;
    font-size: 1.2rem;
  }
}

// Chapter Progress List
.chapter-progress-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.chapter-progress-row {
  display: grid;
  grid-template-columns: 180px 1fr auto auto;
  gap: 1.5rem;
  margin-top: 10px;
  align-items: start;
}

// "Continue here" card — mirrors the LMS student portal treatment: white card,
// amber outline and a floating pill anchored to the top edge.
.continue-here-row {
  position: relative;
  margin-top: 1.75rem;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 1.5px solid #f0a202;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(240, 162, 2, 0.14);
}

.continue-here-badge {
  position: absolute;
  top: -0.7rem;
  left: 1.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.65rem;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #ffffff;
  background: #f0a202;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(240, 162, 2, 0.35);

  i {
    font-size: 0.7rem;
  }
}

.locked-row {
  opacity: 0.7;

  .chapter-row-title,
  .chapter-row-percentage,
  .chapter-row-lessons {
    color: #94a3b8;
  }
}

.chapter-row-lock {
  margin-right: 0.4rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.final-exam-row {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.final-exam-badge {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b21a8;
  background: #f3e8ff;
  border-radius: 999px;
  vertical-align: middle;
}

.chapter-row-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.chapter-row-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8rem;
}

.chapter-row-main {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.chapter-row-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  padding-top: 5px;
}

.chapter-row-progress-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 500px;
}

.chapter-row-progress-bar {
  width: 100%;
  height: 10px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
  
  &.completed {
    background: #d1fae5;
  }
}

.chapter-row-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: inherit;
  transition: width 0.4s ease;
  
  &.completed {
    background: linear-gradient(90deg, #10b981, #059669);
  }
}

.chapter-row-time {
  color: #1e40af;
  background: #dbeafe;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  font-weight: 500;
  font-size: 0.75rem;
  align-self: flex-start;
  
  i {
    font-size: 0.7rem;
  }
}

.chapter-row-percentage {
  font-weight: 600;
  color: #0f172a;
  padding-top: 2px;
  font-size: 0.95rem;
  text-align: right;
  min-width: 70px;
}

.chapter-row-lessons {
  color: #64748b;
  white-space: nowrap;
  font-size: 0.85rem;
  text-align: right;
  min-width: 90px;
  padding-top: 2px;
}

.chapter-row-status {
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  
  &.completed {
    color: #059669;
    background: #d1fae5;
  }
  
  &.in_progress {
    color: #2563eb;
    background: #dbeafe;
  }
  
  &.not_started {
    color: #64748b;
    background: #f1f5f9;
  }
}

.chapter-row-time {
  color: #1e40af;
  background: #dbeafe;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  font-weight: 500;
  font-size: 0.75rem;
  
  i {
    font-size: 0.7rem;
  }
}

// Analytics Section
.analytics-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.analytics-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
}

.analytics-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  font-size: 1.25rem;
  flex-shrink: 0;
  
  &.video {
    background: #dbeafe;
    color: #1e40af;
  }
  
  &.assignment {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.quiz {
    background: #d1fae5;
    color: #065f46;
  }
}

.analytics-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.analytics-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.analytics-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

.analytics-percentage {
  font-size: 0.8rem;
  font-weight: 600;
  color: #10b981;
}

.gradebook-tabs-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
}



.gradebook-tabs {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.45rem;
  margin-bottom: 0.85rem;
  overflow-x: auto;
}

.gradebook-tab {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.45rem 0.35rem 0.35rem;
  border: none;
  background: transparent;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  color: #4b5563;
  font-weight: 600;
  flex: 1 1 160px;
  min-width: 140px;
  justify-content: center;
  transition: color 0.2s ease, border-bottom-color 0.2s ease;

  i {
    font-size: 1rem;
    color: inherit;
    margin-top: 0.05rem;
  }

  &:hover {
    color: #1d4ed8;
  }

  &.active {
    color: #1e3a8a;
    border-bottom-color: #2563eb;
  }
}

.tab-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.tab-label {
  font-size: 0.85rem;
  margin-bottom: 0.2rem;
}

.tab-count {
  font-size: 0.72rem;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.gradebook-tab-meta {
  display: flex;
  justify-content: flex-start;
  gap: 1.25rem;
  font-size: 0.78rem;
  color: #475569;
  margin-bottom: 0.75rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
}


.submissions-table {
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}

.submissions-filters {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
  flex-wrap: wrap;
}

.export-excel-btn {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.85rem;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;

  i {
    font-size: 0.85rem;
  }
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  margin: 0;
  white-space: nowrap;
}

.filter-select {
  min-width: 120px;
  font-size: 0.85rem;
  border-radius: 0.5rem;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  padding: 0.35rem 0.75rem;
  
  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
}

.submissions-table-container {
  width: 100%;
  overflow-x: visible;
}


.submissions-table-grid {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
}

.submissions-table-grid col.col-title {
  width: 28%;
}

.submissions-table-grid col.col-status {
  width: 20%;
}

.submissions-table-grid col.col-weight {
  width: 12%;
}

.submissions-table-grid col.col-score {
  width: 13%;
}

.submissions-table-grid col.col-time {
  width: 14%;
}

.submissions-table-grid col.col-action {
  width: 13%;
}

/* Grant-only (super admin): just enough room for the icon button. */
.submissions-table-grid col.col-action--narrow {
  width: 8%;
}

.submissions-table-grid thead {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  color: #94a3b8;
  font-weight: 600;
}

.submissions-table-grid th,
.submissions-table-grid td {
  padding: 0.75rem 0.65rem;
  vertical-align: middle;
}

.submissions-table-grid th {
  text-align: left;
  white-space: nowrap;
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 2px solid #e2e8f0;
  font-weight: 600;
}


.submissions-table-grid thead th:first-child {
  border-top-left-radius: 0.75rem;
}

.submissions-table-grid thead th:last-child {
  border-top-right-radius: 0.75rem;
}

.submissions-table-grid thead th::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: #f8fafc;
  z-index: -1;
}

.submissions-table-grid tbody tr {
  border-bottom: 1px solid #eef2f6;
}

.submissions-table-grid tbody tr:last-child {
  border-bottom: none;
}

.submissions-table-grid tbody tr:hover {
  background: #fdfefe;
}

.submission-section-row {
  border-bottom: none;
}

.submission-section-row:hover {
  background: transparent !important;
}

.submission-section-cell {
  padding: 0.6rem 0.65rem !important;
  background: #f8fafc;
}

.submission-section-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2f4f75;
}

.submission-title {
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  padding-right: 0.5rem;
}

.submission-title .lesson-title {
  margin: 0 0 0.2rem;
  font-size: 0.72rem;
  color: #5f7391;
  font-weight: 600;
}

.submission-title .title {
  font-weight: 500;
  font-size: 0.85rem;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
  color: #1f2937;
}

.submission-type,
.submission-weight,
.submission-score {
  font-weight: 600;
  color: #0f172a;
}

.submission-weight,
.submission-score,
.submission-time,
.submission-completed {
  text-align: center;
  white-space: nowrap;
  font-size: 0.85rem;
  color: #374151;
  font-weight: 500;
}

.submission-time {
  color: #6b7280;
  font-size: 0.8rem;
}

.submission-completed span {
  display: inline-block;
  white-space: nowrap;
  font-size: 0.8rem;
  color: #374151;
}

.submission-status {
  text-align: left;
  vertical-align: middle;
}

.status-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: flex-start;
}

.completed-date {
  font-size: 0.65rem;
  color: #6b7280;
  line-height: 1.3;
  display: block;
  font-weight: 400;
}

.activity-module {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  margin-top: 1.5rem;
  box-shadow: 0 2px 8px rgba(15, 33, 55, 0.04);
}

.activity-table {
  margin-top: 1.25rem;
}

.activity-table-head,
.activity-table-row {
  display: grid;
  grid-template-columns: minmax(220px, 2fr) 0.8fr 0.8fr 0.8fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
}


.activity-table-head {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.activity-table-row {
  padding: 0.35rem 0;
  border-bottom: 1px solid #e2e8f0;

  &.clickable {
    cursor: pointer;
  }

  &:hover {
    background: #f1f5f9;
  }
}

.activity-title-cell .title {
  font-weight: 600;
  color: #0f172a;
}

.activity-title-cell .subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
  margin-bottom: 0;
}

.video-table {
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}

.video-table-content {
  min-width: 100%;
}

.video-table-head,
.video-table-row {
  display: grid;
  grid-template-columns: minmax(220px, 2fr) 0.9fr 1.1fr;
  gap: 1.25rem;
  align-items: center;
}

.video-table-head {
  padding: 0.85rem 1rem;
  background: #f8fafc;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  color: #94a3b8;
}

.video-table-row {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eef2f6;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fdfefe;
  }
}

.video-title-cell .title {
  font-weight: 600;
  margin: 0;
}

.video-status-cell {
  display: flex;
  align-items: center;
}

.video-date-cell {
  color: #475569;
  font-size: 0.78rem;
}

.activity-status-cell,
.activity-weight-cell,
.activity-score-cell,
.activity-date-cell,
.activity-action-cell {
  font-size: 0.82rem;
  color: #0f172a;
}

.activity-date-cell {
  color: #475569;
  font-size: 0.76rem;
}

/* Every action cell reserves the same width, so the right edge lines up
   whether or not the row carries the grant affordance. */
$action-slot-width: 172px;
$grant-btn-width: 38px;
$grant-only-btn-width: 60px;

.submission-actions-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  width: $action-slot-width;

  /* Grant button only (super admin) — no need to reserve the full slot. */
  &.grant-only {
    width: $grant-only-btn-width;

    .action-split,
    .grant-attempts-btn {
      flex: 0 0 100%;
      width: 100%;
      border-radius: 4px;
    }

    .grant-attempts-btn {
      height: 28px;
    }
  }
}

.action-split {
  display: inline-flex;
  align-items: stretch;
  width: 100%;
}

.view-attempts-btn {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.7rem;
  padding: 0.3rem 0.75rem;
  border-radius: 6px;
  text-transform: capitalize;
  letter-spacing: 0.01em;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #2563eb;
  font-weight: 600;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #1d4ed8;
    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.1);
  }
}

/* Joined split button: square off the inner edges and collapse the shared
   border so the pair reads as one control. */
.action-split.has-grant {
  .view-attempts-btn {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .grant-attempts-btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -1px;
  }
}

.grant-attempts-btn {
  flex: 0 0 $grant-btn-width;
  width: $grant-btn-width;
  padding: 0.3rem 0;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #eff6ff;
  color: #2563eb;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .material-icons {
    font-size: 18px;
    line-height: 1;
  }

  &:hover,
  &:focus {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.1);
  }
}

.submission-action {
  text-align: center;
}

.submissions-table-grid th.action-head {
  text-align: center;
}

.activity-action-cell {
  text-align: right;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  border: 1px solid transparent;

  &.completed {
    background: #d1fae5;
    color: #065f46;
    border-color: #a7f3d0;
  }

  &.pending {
    background: #fef3c7;
    color: #92400e;
    border-color: #fde68a;
  }

  &.in_progress {
    background: #dbeafe;
    color: #1e40af;
    border-color: #bfdbfe;
  }
}

.dash {
  color: #cbd5f5;
}

.card-footer {
  border-top: none;
  padding-top: 1rem;
}

.empty-state {
  padding: 2rem 1rem;
  text-align: center;
  color: #94a3b8;
}

@media (max-width: 992px) {
  .gradebook-tabs {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .gradebook-tab {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 120px;
  }

  .activity-table-head,
  .activity-table-row {
    grid-template-columns: 1fr;
  }

  .activity-action-cell {
    text-align: left;
  }
}

@media (max-width: 768px) {
  .consolidated-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .analytics-section {
    grid-template-columns: 1fr;
  }
  
  .chapter-progress-row {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
  }
  
  .chapter-row-title {
    font-size: 0.9rem;
    padding-top: 0;
  }
  
  .chapter-row-progress-column {
    max-width: 100%;
    order: 2;
  }
  
  .chapter-row-percentage {
    text-align: left;
    order: 3;
    font-size: 0.9rem;
  }
  
  .chapter-row-lessons {
    text-align: left;
    order: 4;
    font-size: 0.8rem;
  }
  
  .chapter-row-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .chapter-row-meta {
    flex-wrap: wrap;
  }
  
  .submissions-table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .submissions-table-grid {
    min-width: 700px;
  }
  
  .submissions-filters {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .video-table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .video-table-content {
    min-width: 600px;
  }
  
  .activity-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .activity-table-head,
  .activity-table-row {
    min-width: 700px;
  }
}

@media (max-width: 576px) {
  .gradebook-surface {
    padding: 1rem;
  }

  .profile-card,
  .overall-progress-card,
  .chapter-progress-section,
  .gradebook-tabs-card {
    padding: 1.25rem;
  }
  
  .consolidated-progress-card {
    padding: 1rem;
  }
  
  .analytics-item {
    padding: 0.75rem;
  }
  
  .analytics-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  .gradebook-tabs {
    flex-direction: column;
    gap: 0.5rem;
    padding-bottom: 0.75rem;
  }
  
  .gradebook-tab {
    width: 100%;
    flex: 1 1 auto;
    min-width: unset;
    justify-content: flex-start;
    padding: 0.65rem 0.75rem;
  }
  
  .tab-copy {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
  
  .tab-count {
    font-size: 0.75rem;
  }
  
  .gradebook-tab-meta {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
}

.font-weight-semibold {
  font-weight: 600;
}

.tracking-wide {
  letter-spacing: 0.12em;
}
</style>
